import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { tr, PLANS_BY_LANG } from "@/lib/i18n";
import {
  useCheckoutConsents,
  CheckoutConsentCheckboxes,
  checkoutPayload,
} from "@/components/CheckoutConsent";
import WhatNextPaywall from "@/components/WhatNextPaywall";

// Actor Profile — private workbook + LEO synthesis. Uses existing Career
// Path visual language (paper / script-input / pill-btn / ghost-btn). Not a
// Career Path — bypasses `check_career_path_quota` on the backend and
// stores in the dedicated `actor_profiles` collection (encrypted at rest).
// LEO NEVER generates or overwrites `alignment_notes` — private notebook.

const SECTIONS = [
  {
    key: "identity",
    titleKey: "actor_profile.section_identity",
    subKey: "actor_profile.section_identity_sub",
    questions: [
      "actor_profile.q_identity_1",
      "actor_profile.q_identity_2",
      "actor_profile.q_identity_3",
      "actor_profile.q_identity_4",
      "actor_profile.q_identity_5",
      "actor_profile.q_identity_6",
      "actor_profile.q_identity_7",
    ],
  },
  {
    key: "direction",
    titleKey: "actor_profile.section_direction",
    subKey: "actor_profile.section_direction_sub",
    questions: [
      "actor_profile.q_direction_1",
      "actor_profile.q_direction_2",
      "actor_profile.q_direction_3",
      "actor_profile.q_direction_4",
      "actor_profile.q_direction_5",
      "actor_profile.q_direction_6",
      "actor_profile.q_direction_7",
      "actor_profile.q_direction_8",
    ],
  },
  {
    key: "work_standard",
    titleKey: "actor_profile.section_work_standard",
    subKey: "actor_profile.section_work_standard_sub",
    questions: [
      "actor_profile.q_work_1",
      "actor_profile.q_work_2",
      "actor_profile.q_work_3",
      "actor_profile.q_work_4",
      "actor_profile.q_work_5",
      "actor_profile.q_work_6",
      "actor_profile.q_work_7",
      "actor_profile.q_work_8",
      "actor_profile.q_work_9",
    ],
  },
];

function EditableSection({ lang, testid, title, sub, value, onSave }) {
  const t = (k) => tr(k, lang);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      toast.error(t("actor_profile.save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid={testid} className="paper p-5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
            {title}
          </div>
          <div className="serif text-2xl text-stone-100 mt-1">{sub}</div>
        </div>
        {!editing && (
          <button
            type="button"
            data-testid={`${testid}-edit`}
            onClick={() => setEditing(true)}
            className="ghost-btn !py-1 !px-2 text-xs"
          >
            <Pencil size={11} /> {t("actor_profile.btn_edit")}
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-3 space-y-3">
          <textarea
            data-testid={`${testid}-textarea`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={8}
            className="script-input w-full resize-none"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid={`${testid}-save`}
              onClick={save}
              disabled={saving}
              className="pill-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin"/> : null}
              {t("actor_profile.btn_save")}
            </button>
            <button
              type="button"
              data-testid={`${testid}-cancel`}
              onClick={() => { setEditing(false); setDraft(value || ""); }}
              disabled={saving}
              className="ghost-btn"
            >
              {t("actor_profile.btn_cancel")}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-stone-300 text-[15px] leading-relaxed mt-3 whitespace-pre-line">
          {value || <span className="text-stone-500 italic">—</span>}
        </p>
      )}
    </section>
  );
}

export default function ActorProfile({ onBack, onCreatePath, onBlank }) {
  const { lang, access, user } = useAuth();
  const recStorageKey = user?.user_id ? `leo_actor_profile_recommendation_${user.user_id}` : null;
  const nav = useNavigate();
  const t = (k) => tr(k, lang);
  const isPaid = ["free", "start", "monthly", "annual", "light"].includes(access?.plan);
  const visibleSections = isPaid ? SECTIONS : SECTIONS.slice(0, 1);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [step, setStep] = useState(0);      // wizard step: 0..N-1
  const [generating, setGenerating] = useState(false);
  const [generatingSlow, setGeneratingSlow] = useState(false);
  const generateAbortRef = useRef(null);
  const [editingAnswers, setEditingAnswers] = useState(false);
  const [busyPlan, setBusyPlan] = useState(null);
  const { consentOf, setConsent, canBuy } = useCheckoutConsents();
  const [answers, setAnswers] = useState({ identity: {}, direction: {}, work_standard: {} });
  const draftTimer = useRef(null);
  const [recommendation, setRecommendation] = useState(null);
  const recRef = useRef(false);
  const [careerPaywallOpen, setCareerPaywallOpen] = useState(false);

  // Preflight against the existing Career Path plan limit — matches
  // `careerAvailable` in CareerPath.jsx. The backend remains the
  // authoritative gate on POST /api/career/goals.
  const careerAvailable = (
    (access?.limits?.career_paths ?? 0) > (access?.usage?.career_paths_used_period ?? 0)
  );

  const hasSynthesis = useMemo(
    () => !!(profile && (profile.identity || profile.direction || profile.work_standard)),
    [profile]
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/actor-profile");
        setProfile(r.data || null);
        if (r.data?.answers && typeof r.data.answers === "object") {
          setAnswers({
            identity:      r.data.answers.identity      || {},
            direction:     r.data.answers.direction     || {},
            work_standard: r.data.answers.work_standard || {},
          });
        }
      } catch {
        toast.error(t("actor_profile.load_error"));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAns = (sectionKey, qKey, val) => {
    setAnswers((a) => {
      const next = { ...a, [sectionKey]: { ...a[sectionKey], [qKey]: val } };
      // FREE users can only draft IDENTITY — autosave that section as
      // draft so leaving the page or hitting the paywall doesn't lose it.
      // Backend PATCH silently drops other sections for FREE anyway.
      if (draftTimer.current) clearTimeout(draftTimer.current);
      draftTimer.current = setTimeout(() => {
        api.patch("/actor-profile", { answers: next })
          .catch(() => { /* draft; ignore transient errors */ });
      }, 700);
      return next;
    });
  };

  const buyPlan = async (planId) => {
    if (!canBuy(planId)) return;
    const suffix = lang === "pl" ? "_pln" : "_usd";
    setBusyPlan(planId);
    try {
      const r = await api.post("/payments/checkout", checkoutPayload(`${planId}${suffix}`));
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "en" ? "Checkout failed" : "Nie udało się utworzyć płatności."));
      setBusyPlan(null);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setGeneratingSlow(false);
    const ctrl = (typeof AbortController !== "undefined") ? new AbortController() : null;
    generateAbortRef.current = ctrl;
    // After 20s show the slow-state block + Cancel/Retry so the user is
    // never trapped on an infinite spinner (backend caps at 25s → 504).
    const slowTimer = setTimeout(() => setGeneratingSlow(true), 20_000);
    try {
      const r = await api.post("/actor-profile/generate", { answers },
        ctrl ? { signal: ctrl.signal } : undefined);
      setProfile(r.data);
      setEditingAnswers(false);
      // One LLM pick of the best-fit Career Path for this actor, grounded
      // in onboarding + Actor Profile. Backend returns one of 4 valid ids.
      try {
        const rec = await api.post("/recommend-career-path", null,
          ctrl ? { signal: ctrl.signal } : undefined);
        setRecommendation(rec.data || null);
        if (recStorageKey && rec.data) {
          try { localStorage.setItem(recStorageKey, JSON.stringify(rec.data)); } catch {}
        }
      } catch { /* non-fatal; profile still saved */ }
    } catch (e) {
      const canceled = e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || e?.message === "canceled";
      if (!canceled) {
        toast.error(e?.response?.data?.detail || t("actor_profile.gen_error"));
      }
    } finally {
      clearTimeout(slowTimer);
      setGeneratingSlow(false);
      setGenerating(false);
      generateAbortRef.current = null;
    }
  };

  const cancelGenerate = () => {
    try { generateAbortRef.current?.abort(); } catch {}
  };

  const patch = async (patchBody) => {
    const r = await api.patch("/actor-profile", patchBody);
    setProfile(r.data);
  };

  // Fetch the recommended Career Path on first entry when a synthesis
  // already exists (user coming back to a previously generated profile).
  // generate() handles the fresh-generation case inline.
  useEffect(() => {
    if (!hasSynthesis || editingAnswers || recommendation || recRef.current) return;
    recRef.current = true;
    // Rehydrate from localStorage first so the recommendation survives
    // returning from the pricing/paywall checkout flow.
    if (recStorageKey) {
      try {
        const raw = localStorage.getItem(recStorageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.recommended_path) {
            setRecommendation(parsed);
            return;
          }
        }
      } catch {}
    }
    (async () => {
      try {
        const r = await api.post("/recommend-career-path");
        setRecommendation(r.data || null);
        if (recStorageKey && r.data) {
          try { localStorage.setItem(recStorageKey, JSON.stringify(r.data)); } catch {}
        }
      } catch { /* non-fatal; UI simply hides the section */ }
    })();
  }, [hasSynthesis, editingAnswers, recommendation, recStorageKey]);

  if (loading) {
    return <div className="mono text-stone-500 text-sm">{t("dash.loading")}</div>;
  }

  // --- Workbook (wizard) ------------------------------------------------
  if (!hasSynthesis || editingAnswers) {
    const s = visibleSections[step];
    const atLastVisible = step >= visibleSections.length - 1;
    // Inline plan picker — same 3-card grid pattern as LEO Radar uses;
    // pulls prices from the single source of truth (PLANS_BY_LANG in
    // i18n.js) and hits the same POST /payments/checkout used everywhere
    // else. NO new paywall design.
    const plans = PLANS_BY_LANG[lang] || PLANS_BY_LANG.pl || [];
    const unlockPlans = ["leo_start", "leo_monthly", "leo_light", "leo_annual"]
      .map((id) => plans.find((p) => p.id === id))
      .filter(Boolean);
    return (
      <div data-testid="actor-profile-wizard" className="space-y-6 max-w-3xl">
        <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
          {t("actor_profile.wizard_progress")
            .replace("{n}", String(step + 1))
            .replace("3", String(visibleSections.length))}
        </div>
        <div>
          <div className="serif text-4xl text-stone-100">{t(s.titleKey)}</div>
          <div className="text-stone-400 text-[15px] mt-1">{t(s.subKey)}</div>
        </div>

        <p className="text-stone-500 text-sm">{t("actor_profile.empty_body")}</p>

        <div className="space-y-5">
          {s.questions.map((qKey, i) => (
            <motion.div
              key={qKey}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }}
            >
              <label className="label-cap">{i + 1}. {t(qKey)}</label>
              <textarea
                data-testid={`ap-q-${s.key}-${i + 1}`}
                value={answers[s.key][qKey] || ""}
                onChange={(e) => setAns(s.key, qKey, e.target.value)}
                placeholder={t("actor_profile.answer_ph")}
                rows={3}
                className="script-input w-full resize-none mt-1"
              />
            </motion.div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                type="button"
                data-testid="ap-btn-back"
                onClick={() => setStep((n) => Math.max(0, n - 1))}
                className="ghost-btn"
              >
                <ChevronLeft size={14}/> {t("actor_profile.btn_back")}
              </button>
            )}
            <button
              type="button"
              data-testid="ap-btn-cancel"
              onClick={() => (editingAnswers ? setEditingAnswers(false) : onBack())}
              className="ghost-btn"
            >
              {editingAnswers ? t("actor_profile.btn_cancel_editing") : t("actor_profile.btn_cancel")}
            </button>
          </div>

          {isPaid && !atLastVisible ? (
            <button
              type="button"
              data-testid="ap-btn-next"
              onClick={() => setStep((n) => Math.min(visibleSections.length - 1, n + 1))}
              className="pill-btn"
            >
              {t("actor_profile.btn_next")} <ChevronRight size={14}/>
            </button>
          ) : isPaid ? (
            <button
              type="button"
              data-testid="ap-btn-generate"
              onClick={generate}
              disabled={generating}
              className="pill-btn"
            >
              {generating
                ? <><Loader2 size={12} className="animate-spin"/> {t("actor_profile.generating")}</>
                : (editingAnswers ? t("actor_profile.btn_update") : t("actor_profile.btn_generate"))}
            </button>
          ) : null}
        </div>

        {generatingSlow && (
          <div
            data-testid="ap-generating-slow"
            className="mt-3 flex flex-wrap items-center gap-3 text-xs text-stone-400"
          >
            <span>{t("actor_profile.generating_slow")}</span>
            <button
              type="button"
              data-testid="ap-btn-cancel-generate"
              onClick={cancelGenerate}
              className="ghost-btn"
            >
              {t("actor_profile.btn_cancel_generation")}
            </button>
          </div>
        )}

        {!isPaid && (
          <section
            data-testid="actor-profile-paywall"
            className="paper p-6 mt-4"
          >
            <h3 className="serif text-2xl sm:text-3xl text-stone-100">
              {t("actor_profile.paywall_title")}
            </h3>
            <p className="text-stone-400 mt-3 text-sm leading-relaxed">
              {t("actor_profile.paywall_subtitle")}
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {unlockPlans.map((plan) => (
                <div
                  key={plan.id}
                  data-testid={`ap-paywall-plan-${plan.id}`}
                  className="border border-stone-800 rounded-2xl p-5 bg-stone-900/60 flex flex-col"
                >
                  <div className="serif text-xl text-stone-100">{plan.name}</div>
                  <div className="mono text-xs uppercase tracking-widest text-amber-400 mt-2">
                    {plan.price}{" "}
                    <span className="text-stone-500">{plan.priceSuffix || ""}</span>
                  </div>
                  <CheckoutConsentCheckboxes
                    lang={lang}
                    planId={plan.id}
                    consent={consentOf(plan.id)}
                    onChange={(k, v) => setConsent(plan.id, k, v)}
                    testidPrefix="ap-paywall-consent"
                  />
                  <div className="flex-1" />
                  <button
                    type="button"
                    data-testid={`ap-paywall-buy-${plan.id}`}
                    disabled={busyPlan === plan.id || !canBuy(plan.id)}
                    onClick={() => buyPlan(plan.id)}
                    className="pill-btn mt-4 w-full disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {busyPlan === plan.id
                      ? (lang === "pl" ? "Otwieram Stripe…" : "Opening Stripe…")
                      : `${lang === "pl" ? "Wybierz" : "Choose"} ${plan.name.replace(/^LEO\s+/i, "")}`}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    );
  }

  // --- Post-synthesis view ---------------------------------------------
  const creators = Array.isArray(profile?.creator_suggestions) ? profile.creator_suggestions : [];
  const openEditAnswers = () => {
    const a = profile?.answers || {};
    setAnswers({
      identity:      a.identity      || {},
      direction:     a.direction     || {},
      work_standard: a.work_standard || {},
    });
    setStep(0);
    setEditingAnswers(true);
    // User chose to manually edit the profile answers — invalidate any
    // cached recommendation so it is re-derived after the next synthesis.
    if (recStorageKey) {
      try { localStorage.removeItem(recStorageKey); } catch {}
    }
    setRecommendation(null);
    recRef.current = false;
  };
  return (
    <div data-testid="actor-profile-view" className="space-y-4 max-w-3xl">
      <div className="flex justify-end">
        <button
          type="button"
          data-testid="ap-btn-edit-answers"
          onClick={openEditAnswers}
          className="pill-btn"
        >
          <Pencil size={12} /> {t("actor_profile.btn_edit_answers")}
        </button>
      </div>
      <EditableSection
        lang={lang}
        testid="ap-section-identity"
        title={t("actor_profile.section_identity")}
        sub={t("actor_profile.section_identity_sub")}
        value={profile?.identity || ""}
        onSave={(v) => patch({ identity: v })}
      />

      <EditableSection
        lang={lang}
        testid="ap-section-direction"
        title={t("actor_profile.section_direction")}
        sub={t("actor_profile.section_direction_sub")}
        value={profile?.direction || ""}
        onSave={(v) => patch({ direction: v })}
      />

      {creators.length > 0 && (
        <section data-testid="ap-creators" className="paper p-5">
          <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
            {t("actor_profile.creators_title")}
          </div>
          <ul className="mt-3 space-y-2">
            {creators.map((c, i) => (
              <li key={`${c.name}-${i}`} className="text-stone-200 text-[15px]">
                <span className="text-stone-100 font-medium">{c.name}</span>
                {c.why ? <span className="text-stone-400"> — {c.why}</span> : null}
              </li>
            ))}
          </ul>
          <p className="text-stone-500 text-sm mt-4 leading-relaxed">
            {t("actor_profile.creators_intro")}
          </p>
        </section>
      )}

      <EditableSection
        lang={lang}
        testid="ap-section-work-standard"
        title={t("actor_profile.section_work_standard")}
        sub={t("actor_profile.section_work_standard_sub")}
        value={profile?.work_standard || ""}
        onSave={(v) => patch({ work_standard: v })}
      />

      <section data-testid="ap-section-alignment" className="paper p-5">
        <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
          {t("actor_profile.section_alignment")}
        </div>
        <div className="serif text-2xl text-stone-100 mt-1">
          {t("actor_profile.section_alignment_sub")}
        </div>
        <p className="text-stone-400 text-sm italic mt-3 leading-relaxed">
          {t("actor_profile.alignment_hint")}
        </p>
        <div className="mt-4">
          <EditableSection
            lang={lang}
            testid="ap-alignment-notes"
            title={""}
            sub={""}
            value={profile?.alignment_notes || ""}
            onSave={(v) => patch({ alignment_notes: v })}
          />
        </div>
      </section>

      {recommendation?.recommended_path && (
        <section data-testid="ap-next-step" className="paper p-6 mt-4">
          <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
            {t("actor_profile.next_step_title")}
          </div>
          <div className="serif text-2xl text-stone-100 mt-2">
            {t(`actor_profile.path_name_${recommendation.recommended_path}`)}
          </div>
          {recommendation.reason && (
            <p className="text-stone-300 text-[15px] leading-relaxed mt-3">
              {recommendation.reason}
            </p>
          )}
          <div className="flex flex-wrap gap-3 mt-5">
            <button
              type="button"
              data-testid="ap-next-step-create"
              onClick={() => {
                // Match CareerPath.jsx access rules — FREE users and
                // paid users without quota see the paywall inline
                // instead of being redirected to /pricing or /career/preview.
                if (access?.plan === "free" || !careerAvailable) {
                  setCareerPaywallOpen(true);
                  return;
                }
                const map = onCreatePath || {};
                const fn = map[recommendation.recommended_path];
                if (typeof fn === "function") fn();
              }}
              className="pill-btn"
            >
              {t("actor_profile.create_this_path")}
            </button>
            <button
              type="button"
              data-testid="ap-next-step-blank"
              onClick={() => {
                // Match CareerPath.jsx access rules for other paths —
                // FREE users and paid users without quota both see the
                // existing WhatNextPaywall instead of a bare redirect.
                if (access?.plan === "free" || !careerAvailable) {
                  setCareerPaywallOpen(true);
                  return;
                }
                nav("/career?view=plan");
              }}
              className="ghost-btn"
            >
              {t("actor_profile.enter_own_path")}
            </button>
          </div>
        </section>
      )}

      <div className="pt-2 flex items-center gap-3 flex-wrap">
        <button
          data-testid="ap-btn-back-to-career"
          onClick={onBack}
          className="ghost-btn"
        >
          <ChevronLeft size={14}/> {t("actor_profile.btn_back")}
        </button>
        <button
          type="button"
          data-testid="ap-btn-edit-answers-bottom"
          onClick={openEditAnswers}
          className="pill-btn"
        >
          <Pencil size={12} /> {t("actor_profile.btn_edit_answers")}
        </button>
      </div>

      <WhatNextPaywall
        open={careerPaywallOpen}
        kind="career"
        lang={lang}
        onClose={() => setCareerPaywallOpen(false)}
        onSeeAllPlans={() => { setCareerPaywallOpen(false); nav("/pricing"); }}
      />
    </div>
  );
}
